import type { FC } from 'react'

import { Card, StyledText } from '@skytech/ui'
import { uniq } from '@skytech/utils/list-operations'
import { useQuery } from '@tanstack/react-query'

import { Translation } from '@/app/translations/Translation'
import { FormattedNumber } from '@/components/FormattedNumber'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { NoResults } from '@/components/NoResults'

import queries from '../queries'
import adminResources from '../resources'
import type { Asset } from '../resources'

import { monthDiff } from './helpers'

import './AssetStats.styles.scss'

type AgeCategoryKey =
  | '48+ months'
  | '36-48 months'
  | '24-36 months'
  | '12-24 months'
  | '6-12 months'
  | '3-6 months'
  | '0-3 months'
  | 'Missing date'

export const AssetStats: FC<{
  filteredAssetList: Asset[]
  queryAssetType: string | null
  queryAssetAge: string | null
  onSetAssetTypeFilter: (arg: string) => void
  onSetAssetAgeFilter: (arg: string) => void
}> = ({
  filteredAssetList,
  queryAssetAge,
  queryAssetType,
  onSetAssetAgeFilter,
  onSetAssetTypeFilter,
}) => {
  const { data } = useQuery(queries.adminAssetList(), adminResources.getAssetList)
  const unfilteredAssetTypes = uniq(data?.map(asset => asset.assetType))
  const assetsStatsByType = filteredAssetList.reduce<Record<string, Asset[]>>((acc, asset) => {
    acc[asset.assetType] = acc[asset.assetType] ? [...acc[asset.assetType], asset] : [asset]
    return acc
  }, {})

  const statsByAge = filteredAssetList.reduce<Record<AgeCategoryKey, [number, number]>>(
    (acc, asset) => {
      let key: AgeCategoryKey = '48+ months'
      const diff = monthDiff(new Date(asset.received || ''), new Date())
      if (diff <= 48) key = '36-48 months'
      if (diff <= 36) key = '24-36 months'
      if (diff <= 24) key = '12-24 months'
      if (diff <= 12) key = '6-12 months'
      if (diff <= 6) key = '3-6 months'
      if (diff <= 3) key = '0-3 months'
      if (!asset.received) key = 'Missing date'

      acc[key][0]++
      acc[key][1] += asset.cost || 0
      return acc
    },
    {
      'Missing date': [0, 0],
      '0-3 months': [0, 0],
      '3-6 months': [0, 0],
      '6-12 months': [0, 0],
      '12-24 months': [0, 0],
      '24-36 months': [0, 0],
      '36-48 months': [0, 0],
      '48+ months': [0, 0],
    }
  )

  return (
    <div className="asset-stats">
      <div className="asset-stats-category">
        <h2>
          <Translation id="MY ASSETS" />
        </h2>

        {filteredAssetList.length === 0 ? (
          <div className="no-results-wrapper">
            <NoResults />
          </div>
        ) : (
          <div className="asset-stats-grid">
            {unfilteredAssetTypes.map(type => {
              const assets = assetsStatsByType[type] || []
              return (
                <button
                  key={type}
                  className={`asset-stats-box-button ${
                    type === queryAssetType ? 'is-selected' : ''
                  }`}
                  onClick={() => onSetAssetTypeFilter(queryAssetType === type ? '' : type)}
                >
                  <Card hasColoredShadow={queryAssetType === type} className="asset-stats-box">
                    <h3>
                      <Translation id={`asset_type/${type}`} />
                    </h3>

                    <StyledText colorMode={queryAssetType === type ? undefined : 'black'}>
                      <h1>
                        <FormattedNumber amount={assets.length} />
                      </h1>
                    </StyledText>

                    <h5>
                      <FormattedNumberWithCurrency
                        amount={assets.reduce((acc, asset) => acc + (asset.cost || 0), 0)}
                      />
                    </h5>
                  </Card>
                </button>
              )
            })}
          </div>
        )}
      </div>

      <div className="asset-stats-category-divider" />

      <div className="asset-stats-category">
        <h2>
          <Translation id="AGE DIVIDED OVERVIEW" />
        </h2>

        <div className="asset-stats-grid">
          {Object.entries(statsByAge).map(([categoryKey, [assetNumber, assetValue]]) => (
            <button
              key={categoryKey}
              className={`asset-stats-box-button ${
                categoryKey === queryAssetAge ? 'is-selected' : ''
              }`}
              onClick={() => onSetAssetAgeFilter(queryAssetAge === categoryKey ? '' : categoryKey)}
            >
              <Card
                key={categoryKey}
                hasColoredShadow={categoryKey === queryAssetAge}
                className="asset-stats-box"
              >
                <h3>
                  <Translation id={categoryKey} />
                </h3>

                <StyledText colorMode={categoryKey === queryAssetAge ? undefined : 'black'}>
                  <h1>
                    <FormattedNumber amount={assetNumber} />
                  </h1>
                </StyledText>

                <h5>
                  <FormattedNumberWithCurrency amount={assetValue} />
                </h5>
              </Card>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
